import { IApiMessageDto, IApiSubMessageDto } from '@/services/swagger/easourcing/models';
import { BlockType, MsgStreamChunk } from './chunk';

export enum MsgSenderRole {
  User = 'USER',
  Assistant = 'ASSISTANT',
}
/**
 * 消息流完成状态
 */
export enum MsgStreamFinished {
  /** 未完成 */
  unfinished = 0,
  /** 正常完成 */
  normalFinished = 1,
  /** stop终止 */
  stopped = 2,
}
export enum SubContentType {
  /** THOUGHT,CONTENT,SEARCH_CARD,INQUIRY_CARD,REPLY,TASK_COMPLETE */
  thought = 'THOUGHT',
  content = 'CONTENT',
  searchCard = 'SEARCH_CARD',
  inquiryCard = 'INQUIRY_CARD',
  reply = 'REPLY',
  taskComplete = 'TASK_COMPLETE',
  /**
   * 创建要约任务，后端会生成这个用户侧消息
   */
  inquiryAsk = 'INQUIRY_ASK',
  /**
   * ai 草稿邮件
   */
  emailSuggestion = 'EMAIL_SUGGESTION',
  /**
   * 用户重试邮件消息内容
   */
  emailRetryAsk = 'EMAIL_RETRY_ASK',
  /*
   * 错误
   */
  error = 'ERROR',
  /**
   * 行业报告卡片
   */
  marketInsightReport = 'REPORT_CARD',
  /**
   * 供应商搜索卡片 V2
   */
  searchCardV2 = 'SEARCH_RESULT_CARD',
  /**
   * Insight Chip（由轮询接口 /api/biz/session/insight-chip 触发，插入到 sub message list）
   */
  insightChip = 'INSIGHT_CHIP',
}

/**
 * 支持打字机效果的消息块
 */
export const typingBlockTypes: Set<SubContentType> = new Set([SubContentType.thought, SubContentType.content]);
/**
/**
 * 供应商卡片块
 */
export interface CompanyCardBlock {
  blockType: BlockType.CompanyCard;
  data: Record<string, unknown>;
}

/**
 * 初始化ai消息实体
 */
export const initMsgDto = (messageId: string): IApiMessageDto => {
  return {
    messageId,
    createTime: Date.now(),
    streamFinished: MsgStreamFinished.unfinished,
    like: 0,
    senderRole: MsgSenderRole.Assistant,
    subMessage: [],
  };
};

/**
 * 添加MsgChunk到AiMsg
 *
 * 支持三种处理路径：
 * 1. SUB_MESSAGE_CHUNK + 新 subMessageId → 创建占位子消息，text = chunk
 * 2. SUB_MESSAGE_CHUNK + 已有 subMessageId → text += chunk 追加文本
 * 3. SUB_MESSAGE + 已有 subMessageId → 用完整 contentValue 覆盖（后端权威数据）
 * 4. SUB_MESSAGE + 新 subMessageId → 直接追加（卡片类型无前置 chunk）
 */
export const appendMsgChunkToAiMsg = (msg: IApiMessageDto, chunk: MsgStreamChunk): IApiMessageDto | undefined => {
  try {
    if (chunk.event === 'SUB_MESSAGE_CHUNK') {
      const { subMessageId, contentType, chunk: text } = chunk.data;
      const preSubMessage = msg?.subMessage || [];
      const existIndex = preSubMessage.findIndex(item => item.subMessageId === subMessageId);

      if (existIndex === -1) {
        // 第一个 chunk：创建新子消息占位
        const newSubMsg = { subMessageId, contentType, contentValue: { text } } as IApiSubMessageDto;
        return { ...msg, subMessage: [...preSubMessage, newSubMsg] };
      } else {
        // 后续 chunk：追加文本到已有子消息
        const updated = preSubMessage.map((item, i) => {
          if (i !== existIndex) return item;
          const prevText = (item.contentValue as { text?: string })?.text || '';
          return { ...item, contentValue: { ...(item.contentValue as object), text: prevText + text } };
        });
        return { ...msg, subMessage: updated };
      }
    }

    if (chunk.event === 'SUB_MESSAGE') {
      const { data } = chunk;
      const preSubMessage = msg?.subMessage || [];
      const existIndex = preSubMessage.findIndex(item => item.subMessageId === data.subMessageId);

      if (existIndex !== -1) {
        // 用完整 contentValue 覆盖已累积的 chunk 文本（后端权威数据）
        const updated = preSubMessage.map((item, i) =>
          i === existIndex ? { ...item, ...(data as unknown as IApiSubMessageDto) } : item
        );
        return { ...msg, subMessage: updated };
      } else {
        // 无前置 chunk（卡片类型等）：直接追加
        return { ...msg, subMessage: [...preSubMessage, data as unknown as IApiSubMessageDto] };
      }
    }
  } catch (error) {
    console.error('appendMsgChunkToAiMsg error', error);
    return msg;
  }
  return msg;
};
export { BlockType };
